import type { ArgumentsSchema } from '@lighthouse/core'
import { useAtomData } from '@lighthouse/shared'
import { useCallback, useMemo } from 'react'

import { CURRENT_PAGE, HOME_PAGE } from '@/atoms/application/constants'
import { appAtom, sharePageAtom } from '@/atoms/application/state'
import { languagePageListAtom, lastPageTypeOfStackAtom } from '@/atoms/page/state'

import { useCurrentLanguagePageList } from './usePage'

type Option = {
    isCurrentPage?: boolean
    isProdHost?: boolean
}

export function usePageURI(option?: Option) {
    const { isCurrentPage = true, isProdHost } = option ?? {}
    const lastPageTypeOfStack = useAtomData(lastPageTypeOfStackAtom)
    const pageList = useCurrentLanguagePageList()
    const sharePageId = useAtomData(sharePageAtom)
    const sharePage = useMemo(() => pageList.find(item => item.id === sharePageId), [pageList, sharePageId])
    const homePage = useAtomData(
        languagePageListAtom,
        useCallback(list => list.find(p => p.isHome), [])
    )

    const host = useAtomData(
        appAtom,
        useCallback(
            s => {
                return isProdHost
                    ? s.config.domain.isUsedCustomDomain && s.config.domain.customDomain
                        ? s.config.domain.customDomain
                        : `${s.config.domain.domainPrefix}.${s.mainDomain}`
                    : s.version.url
            },
            [isProdHost]
        )
    )

    const shareUrl = useMemo(() => {
        if (sharePageId === CURRENT_PAGE || isCurrentPage) {
            if (!lastPageTypeOfStack) {
                return host
            }

            const page = pageList.find(item => item.id === lastPageTypeOfStack.pageId)
            if (!page) {
                return host
            }

            const pathSuffix = generatePageSuffix(
                page.parameterAndVariable?.pathParameter,
                page.parameterAndVariable?.queryParameter,
                lastPageTypeOfStack.pagePathParameters,
                lastPageTypeOfStack.pageQueryParameters
            )

            // const path = page.route ? `/${page.route}` : `/${page.id}`
            const path = `/${page.route || ''}`

            return `${host}${path + pathSuffix}`
        }
        if (sharePageId === HOME_PAGE && homePage) {
            // return homePage.route ? `${host}/${homePage.route}` : `${host}/${homePage.id}`
            return `${host}/${homePage.route || ''}`
        }
        // return sharePage?.route ? `${host}/${sharePage.route}` : `${host}/${sharePageId}`
        return `${host}/${sharePage?.route || ''}`
    }, [homePage, host, pageList, isCurrentPage, sharePage?.route, sharePageId, lastPageTypeOfStack])

    return useMemo(() => ({ shareUrl: `https://${shareUrl}`, host }), [host, shareUrl])
}

/**
 * @TODO 放到别的地方去，然后生成地址的地方都用这个，通用的生成url逻辑
 * 根据stack 路由参数和页面schema生成路由参数地址
 *
 * @param {(ArgumentsSchema[] | undefined)} pagePathSchemas
 * @param {(ArgumentsSchema[] | undefined)} pageQuerySchemas
 * @param {?string[]} [pagePathParameters]
 * @param {?Record<string, string>} [pageQueryParameters]
 * @returns {string}
 */
function generatePageSuffix(
    pagePathSchemas: ArgumentsSchema[] | undefined,
    pageQuerySchemas: ArgumentsSchema[] | undefined,
    pagePathParameters?: string[],
    pageQueryParameters?: Record<string, string>
) {
    const path = pagePathParameters?.join('/') || ''
    const queryData = pageQueryParameters
        ? Object.fromEntries(
              Object.entries(pageQueryParameters)
                  .map(([k, v]) => {
                      const schema = pageQuerySchemas?.find(item => item.id === k)
                      if (!schema) {
                          return false
                      }

                      return [schema.name, v]
                  })
                  .filter(Boolean) as unknown as [string, string][]
          )
        : undefined
    const search = new URLSearchParams(queryData).toString()

    return `${path ? `/${path}` : ''}${search ? `?${search}` : ''}`
}
